import { SupabaseClient } from "@supabase/supabase-js";
import { SearchFilters } from "../context/SearchTypes";
import { isDestinationInPriceRange } from './costRangeUtils';
import { supabase } from '@/lib/supabase';
import { Destination } from "../context/SearchTypes";

export const buildDestinationQuery = (
  supabase: SupabaseClient,
  searchQuery: string,
  filters: SearchFilters,
  page: number = 1,
  itemsPerPage: number = 9,
  fetchAll: boolean = false
) => {
  let baseQuery = supabase
    .from('destinations')
    .select(`
      *,
      countries!destinations_country_code_fkey (
        name,
        code
      )
    `, { count: 'exact' });
    
  // Apply text search if provided
  if (searchQuery && searchQuery.trim() !== '') {
    const searchTerms = searchQuery.trim().split(' ').map(term => term.toLowerCase());
    
    baseQuery = baseQuery.or(
      searchTerms.map(term => 
        `name.ilike.%${term}%,` +
        `description.ilike.%${term}%,` +
        `city_name.ilike.%${term}%,` +
        `country.ilike.%${term}%,` +
        `country_code.ilike.%${term}%,` +
        `region.ilike.%${term}%`
      ).join(',')
    );
  }

  // Apply climate filter
  if (filters.climate) {
    switch (filters.climate) {
      case 'winter':
        baseQuery = baseQuery.or(
          `climate_data->>best_visit_season.ilike.%december%,` +
          `climate_data->>best_visit_season.ilike.%january%,` +
          `climate_data->>best_visit_season.ilike.%february%`
        );
        break;
      case 'spring':
        baseQuery = baseQuery.or(
          `climate_data->>best_visit_season.ilike.%march%,` +
          `climate_data->>best_visit_season.ilike.%april%,` +
          `climate_data->>best_visit_season.ilike.%may%`
        );
        break;
      case 'summer':
        baseQuery = baseQuery.or(
          `climate_data->>best_visit_season.ilike.%june%,` +
          `climate_data->>best_visit_season.ilike.%july%,` +
          `climate_data->>best_visit_season.ilike.%august%`
        );
        break;
      case 'fall':
      case 'autumn':
        baseQuery = baseQuery.or(
          `climate_data->>best_visit_season.ilike.%september%,` +
          `climate_data->>best_visit_season.ilike.%october%,` +
          `climate_data->>best_visit_season.ilike.%november%`
        );
        break;
      case 'year_round':
        baseQuery = baseQuery.or(
          `climate_data->>best_visit_season.ilike.%all year%,` +
          `climate_data->>best_visit_season.ilike.%year round%,` +
          `climate_data->>best_visit_season.ilike.%any time%`
        );
        break;
    }
  }

  // Apply beach filter
  if (filters.hasBeach !== null) {
    baseQuery = baseQuery.eq('has_beach', filters.hasBeach);
  }

  // Apply safety filter
  if (filters.safety) {
    baseQuery = baseQuery.gte('sustainability_safety.safety_rating', filters.safety);
  }

  // Apply region filter
  if (filters.region) {
    baseQuery = baseQuery.eq('region', filters.region);
  }

  // We'll fetch ALL results to apply price filtering on client-side
  // This ensures consistent pagination after price filtering
  // Only apply server-side pagination if fetchAll is false AND we're not filtering by price
  if (!fetchAll && (!filters.priceRange || filters.priceRange[0] === 0 && filters.priceRange[1] === 0)) {
    // Ensure page is at least 1
    const validPage = Math.max(1, page);
    const from = (validPage - 1) * itemsPerPage;
    const to = from + itemsPerPage - 1;
    console.log(`Applying pagination: page ${validPage}, from ${from}, to ${to}`);
    baseQuery = baseQuery.range(from, to);
  }

  return baseQuery;
};

export const buildHostQuery = (
  supabase: SupabaseClient, 
  searchQuery: string,
  page: number = 1,
  itemsPerPage: number = 9
) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return null;
  }

  const hostsQuery = supabase
    .from('profiles')
    .select(`
      *,
      host_destinations!inner (
        destinations (
          id,
          name,
          country,
          city_name
        )
      )
    `, { count: 'exact' })
    .eq('role', 'host')
    .eq('is_verified', true); // Only fetch verified hosts

  // Add search terms
  const searchTerms = searchQuery.trim().split(' ');
  const termFilters = searchTerms.map(term => {
    return `username.ilike.%${term}%,bio.ilike.%${term}%,location.ilike.%${term}%,nationality.ilike.%${term}%`;
  });
  
  // Apply the filters
  if (termFilters.length > 0) {
    hostsQuery.or(termFilters.join(','));
  }
  
  // Apply pagination
  const from = (page - 1) * itemsPerPage;
  const to = from + itemsPerPage - 1;
  hostsQuery.range(from, to);
  
  return hostsQuery;
};

export const applyClientSideFilters = (destinations: any[], filters: SearchFilters) => {
  console.log('Applying client-side filters:', filters);
  console.log('Initial destinations count:', destinations.length);
  
  let filtered = [...destinations];
  
  // Price filtering
  if (filters.priceRange && Array.isArray(filters.priceRange) && filters.priceRange.length === 2) {
    const min = Number(filters.priceRange[0]);
    const max = Number(filters.priceRange[1]);
    
    if (!isNaN(min) && !isNaN(max)) {
      console.log(`Filtering by price range [${min}, ${max}] with costType: ${filters.costType}`);
      
      filtered = filtered.filter(destination => 
        isDestinationInPriceRange(
          destination, 
          filters.costType || 'avg_accommodation_cost', 
          [min, max] as [number, number]
        )
      );
      
      console.log('After price filtering destinations count:', filtered.length);
    } else {
      console.warn('Invalid price range values:', filters.priceRange);
    }
  }
  
  return filtered;
};

export const applySorting = (destinations: any[], sortBy: string, costType?: string) => {
  const sorted = [...destinations];
  
  if (sortBy) {
    switch (sortBy.toLowerCase()) {
      case 'daily budget low to high':
        sorted.sort((a, b) => {
          // Ensure we're comparing numbers
          const costRawA = a.costs_infrastructure?.[costType];
          const costRawB = b.costs_infrastructure?.[costType];
          
          const costA = typeof costRawA === 'number' 
            ? costRawA 
            : typeof costRawA === 'string' 
              ? parseFloat(costRawA)
              : 0;
              
          const costB = typeof costRawB === 'number' 
            ? costRawB 
            : typeof costRawB === 'string' 
              ? parseFloat(costRawB)
              : 0;
              
          return costA - costB;
        });
        break;
      case 'population high to low':
        sorted.sort((a, b) => Number(b.population || 0) - Number(a.population || 0));
        break;
      case 'safety rating high to low':
        sorted.sort((a, b) => {
          const safetyRawA = a.sustainability_safety?.safety_rating;
          const safetyRawB = b.sustainability_safety?.safety_rating;
          
          const safetyA = typeof safetyRawA === 'number' 
            ? safetyRawA 
            : typeof safetyRawA === 'string' 
              ? parseFloat(safetyRawA)
              : 0;
              
          const safetyB = typeof safetyRawB === 'number' 
            ? safetyRawB 
            : typeof safetyRawB === 'string' 
              ? parseFloat(safetyRawB)
              : 0;
              
          return safetyB - safetyA;
        });
        break;
      case 'internet speed high to low':
        sorted.sort((a, b) => {
          const speedRawA = a.costs_infrastructure?.internet_speed_avg;
          const speedRawB = b.costs_infrastructure?.internet_speed_avg;
          
          const speedA = typeof speedRawA === 'number' 
            ? speedRawA 
            : typeof speedRawA === 'string' 
              ? parseFloat(speedRawA)
              : 0;
              
          const speedB = typeof speedRawB === 'number' 
            ? speedRawB 
            : typeof speedRawB === 'string' 
              ? parseFloat(speedRawB)
              : 0;
              
          return speedB - speedA;
        });
        break;
      case 'recently updated':
        sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at);
          const dateB = new Date(b.updated_at);
          return dateB.getTime() - dateA.getTime();
        });
        break;
      default:
        sorted.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB.getTime() - dateA.getTime();
        });
    }
  } else {
    // Default sort by created_at
    sorted.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB.getTime() - dateA.getTime();
    });
  }
  
  return sorted;
};
